import type { RefObject } from 'react';
import { useCallback, useLayoutEffect, useState } from 'react';

export function useIsOverflow(ref: RefObject<HTMLElement>, isDisabled?: boolean): boolean {
    const [isOverflow, setIsOverflow] = useState(false);

    const checkOverflow = useCallback(() => {
        if (!ref.current) return;
        const hasOverflow = ref.current.scrollWidth > ref.current.clientWidth;
        setIsOverflow((prev) => (prev === hasOverflow ? prev : hasOverflow));
    }, [ref]);

    useLayoutEffect(() => {
        if (!ref.current || isDisabled) return undefined;

        checkOverflow();

        // Use a variable instead of directly checking in the if condition to
        // prevent issues TypeScript type narrowing where the type of window
        // might be considered unknown after the if check.
        const hasResizeObserver = 'ResizeObserver' in window;

        if (hasResizeObserver) {
            const resizeObserver = new ResizeObserver(checkOverflow);
            resizeObserver.observe(ref.current);

            return () => resizeObserver.disconnect();
        }

        // Fallback for older browsers.
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [ref, isDisabled, checkOverflow]);

    return isOverflow;
}
